import { NEED_REASON_TEMPLATE } from '@act/react-components/needs/constants';
import {
  CreateNeedFormDto,
  CreateNeedFormSchema,
  FilterOptionDto,
  NeedDto,
  NeedsCategoryDto,
  NeedsIssueDto,
  UpdateNeedFormDto,
  UpdateNeedFormSchema,
} from '@act/shared/data-transfer-objects';
import { Alert, FormBuilder } from '@act/storybook';
import { zodResolver } from '@hookform/resolvers/zod';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Dialog, DialogContent, Divider, Typography } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import { TrackingProvider } from 'react-components/tracking';
import { useForm } from 'react-hook-form';
import { needRequests } from 'redux/requests/need.requests';
import {
  selectNeedsCategoryByIdFromMetadata,
  selectNeedsIssueByIdFromMetadata,
  selectNeedsMetadata,
  selectNeedsZcodeByIdFromMetadata,
} from 'redux/slices/metaSlice.slice';
import { metaSliceSelectors } from 'redux/slices/metaSlice.hook';
import { createNeedSuccess, patchNeedSuccess } from 'redux/slices/needs.slice';
import { selectSelectedMemberId } from 'redux/slices/selectedMemberSlice.slice';
import { useAppDispatch, useAppSelector } from 'redux/storeHooks';
import { useApiCall } from 'redux/utils/apiHook.hook';
import { useFeatureFlag } from 'redux/utils/feature-flags/featureFlagHook.hook';
import { FormConfig } from 'stories/forms/FormBuilder/formBuilder.types';
import { GenericDialogProps } from 'utilities/dialog-context/DialogContextHelpers';
import { useToast } from '../app/ReusableToast';
import CloseableDialogTitle from './components/dialog-components/CloseableDialogTitle';
import SubmittableDialogActions from './components/dialog-components/SubmittableDialogActions';
import useRichMessageDialog from './hooks/useRichMessageDialog.hook';
import useUnsavedChangesDialog from './hooks/useUnsavedChangesDialog.hook';
import ZCodeContent from './LearnMoreZcodeContent';

export interface CreateNeedFormDialogUniqueProps {
  need?: NeedDto;
}

export type CreateNeedFormDialogProps = GenericDialogProps<null> &
  CreateNeedFormDialogUniqueProps;
export type CreateNeedFormDialogCallbackInput = null;

export const CreateNeedFormDialog = ({
  need,
  open,
  setOpen,
}: CreateNeedFormDialogProps) => {
  const openLearnMoreDialog = useRichMessageDialog();
  const handleUnsavedChanges = useUnsavedChangesDialog();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const needsDescriptionFF = useFeatureFlag('needs-default-description');
  const defaultReason = needsDescriptionFF
    ? NEED_REASON_TEMPLATE
    : '<p><br></p>';
  const needsMetadata = useAppSelector(selectNeedsMetadata);
  const selectedMemberId = useAppSelector(selectSelectedMemberId);
  const needIssueOptions = metaSliceSelectors.useSelectNeedIssueOptions();

  const [createdNeedResult, isCreatingNeed, createNeed] = useApiCall(
    needRequests.createNeed
  );
  const [updateNeedResult, isPatchingNeed, patchNeed] = useApiCall(
    needRequests.patchNeed
  );
  const isEditing = !!need;
  const isSubmitting = isCreatingNeed || isPatchingNeed;

  const { control, handleSubmit, watch, formState, setValue, reset } = useForm<
    CreateNeedFormDto | UpdateNeedFormDto
  >({
    resolver: zodResolver(need ? UpdateNeedFormSchema : CreateNeedFormSchema),
    defaultValues: {
      issueId: need?.issueId ?? undefined,
      reason: need?.reason ?? defaultReason,
    },
  });
  const { dirtyFields, isValid, isDirty } = formState;
  const formWatch = watch();
  const watchIssue = watch('issueId');

  const [selectedCategory, setSelectedCategory] =
    useState<NeedsCategoryDto>(null);
  const [selectedIssue, setSelectedIssue] = useState<NeedsIssueDto>(null);

  const selectedIssueZCode = selectNeedsZcodeByIdFromMetadata(
    needsMetadata?.data,
    selectedIssue?.zcodeId
  );

  const formConfig: FormConfig<CreateNeedFormDto | UpdateNeedFormDto> = useMemo(
    () => [
      {
        type: 'input',
        name: 'issueId',
        label: 'Search for need',
        input: 'autocomplete',
        props: {
          options: needIssueOptions,
          getOptionLabel: (option: FilterOptionDto) => option?.label || '',
          required: true,
          groupBy: (option: FilterOptionDto) => option?.tagCloud[0],
          startAdornment: <SearchIcon />,
          trackingName: 'create-need-dialog-issue-autocomplete',
          sensitive: false,
        },
      },
      {
        type: 'passthrough' as const,
        content: selectedCategory ? (
          <Box>
            <Alert
              severity="info"
              variant="tonal"
              title={selectedCategory?.label}
              trackingName="create-need-dialog-time-window-alert"
              action={{
                label: 'Learn more',
                onClick: () =>
                  openLearnMoreDialog({
                    title: selectedCategory?.label,
                    content: <ZCodeContent zcode={selectedIssueZCode} />,
                  }),
              }}
            >
              {`This issue has a target time window of ≤ ${selectedIssue?.defaultTimeFrameInDays} days.`}
            </Alert>
            <Divider sx={{ margin: '16px 0' }} />
          </Box>
        ) : null,
      },
      {
        type: 'label' as const,
        label: needsDescriptionFF
          ? 'What do you understand about this member’s situation? (required)'
          : 'What is the reason for pursuing this need? (required)',
      },
      {
        type: 'input',
        name: 'reason',
        label: 'Reason for pursuing this need (required)',
        input: 'richText',
        props: {
          placeholderText: needsDescriptionFF
            ? NEED_REASON_TEMPLATE
            : 'Reason for pursuing this need (required)',
          required: true,
          defaultValue: need?.reason ?? defaultReason,
        },
      },
    ],
    [
      need,
      needIssueOptions,
      selectedCategory,
      selectedIssue,
      selectedIssueZCode,
      openLearnMoreDialog,
    ]
  );

  useEffect(() => {
    if (watchIssue) {
      const issue = selectNeedsIssueByIdFromMetadata(
        needsMetadata?.data,
        watchIssue
      );
      const category = selectNeedsCategoryByIdFromMetadata(
        needsMetadata?.data,
        issue?.categoryId
      );
      setSelectedIssue(issue);
      setSelectedCategory(category);
    }
  }, [watchIssue]);

  useEffect(() => {
    if (need) {
      setValue('issueId', need.issueId);
      setValue('reason', need.reason);
    } else {
      setValue('issueId', undefined);
      setValue('reason', defaultReason);
    }
  }, [need, setValue]);

  useEffect(() => {
    if (createdNeedResult || updateNeedResult) {
      if (createdNeedResult) {
        dispatch(createNeedSuccess(createdNeedResult));
        toast('Need created successfully');
      }
      if (updateNeedResult) {
        dispatch(patchNeedSuccess(updateNeedResult));
        toast('Need updated successfully');
      }
      setOpen(false);
    }
  }, [createdNeedResult, updateNeedResult]);

  async function handleSubmission() {
    // Prevent multiple submissions
    if (isSubmitting) {
      return;
    }

    if (isEditing) {
      const body = {
        ...(Object.keys(dirtyFields).includes('issueId') && {
          issueId: formWatch.issueId,
        }),
        ...(Object.keys(dirtyFields).includes('reason') && {
          reason: formWatch.reason,
        }),
      };

      if (Object.keys(body).length) {
        patchNeed({
          body,
          params: { needId: need.id },
        });
      }
    } else {
      createNeed({
        body: {
          memberId: selectedMemberId,
          issueId: formWatch.issueId,
          reason: formWatch.reason,
        },
        params: null,
      });
    }
  }

  const handleClose = () => {
    // Prevent closing during submission
    if (isSubmitting) {
      return;
    }

    if (isDirty) {
      void handleUnsavedChanges(() => {
        reset();
        setOpen(false);
      });
    } else {
      reset();
      setOpen(false);
    }
  };

  return (
    <TrackingProvider contextName="create-need-form-dialog">
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        onSubmit={handleSubmit(handleSubmission)}
      >
        <CloseableDialogTitle
          title={isEditing ? 'Edit need' : 'Add identified need'}
          handleClose={handleClose}
        />
        <DialogContent>
          <Box>
            <Box sx={{ display: 'block' }}>
              <Typography
                variant="titleMedium"
                sx={{ fontWeight: 500, fontSize: '16px' }}
              >
                What is the member's need?
              </Typography>
            </Box>
            <FormBuilder
              config={formConfig}
              control={control}
              formState={formState}
              disabled={isSubmitting}
            />
          </Box>
        </DialogContent>
        <SubmittableDialogActions
          cancelText={'Cancel'}
          submissionText={isSubmitting ? 'Saving...' : 'Save and close'}
          isValid={isValid}
          disabled={isSubmitting}
          handleClose={handleClose}
          handleSubmission={handleSubmission}
        />
      </Dialog>
    </TrackingProvider>
  );
};

export default CreateNeedFormDialog;
